<div *transloco="let t">
	<div
		mat-dialog-title
		cdkDrag
		cdkDragHandle
		cdkDragRootElement=".cdk-overlay-pane"
		cdkDragBoundary=".cdk-overlay-container"
		class="flex cursor-move items-center justify-between">
		<h3>{{ t('permissions.permissions') }} {{ t('common.for') }} {{ roleTypeLabel || (permissionType ? t('enum.' + permissionType) : '-') }}</h3>
		<div class="flex-1"></div>
		<button aria-label="close dialog" (click)="close()" mat-icon-button>
			<mat-icon>close</mat-icon>
		</button>
	</div>
	<mat-dialog-content>
		<div class="mx-auto max-h-80 w-full overflow-y-auto">
			@if (loading) {
				<div class="flex items-center justify-center px-3 py-10">
					<mat-progress-spinner color="primary" mode="indeterminate" diameter="36"></mat-progress-spinner>
				</div>
			} @else if (permissions.length > 0 && currentUserId) {
				<table @stagger [dataSource]="dataSource" mat-table name="permissions-table-cy">
					<!--- Note that these columns can be defined in any order.
						  The actual rendered columns are set as a property on the row definition" -->

					<ng-container matColumnDef="name">
						<th *matHeaderCellDef mat-header-cell>{{ t('common.name') }}</th>
						<td [attr.data-label]="t('common.name')" *matCellDef="let element" mat-cell>
							<div class="flex flex-col leading-[1.2]">
								<ara-popover [content]="((element.firstName ?? '') + ' ' + (element.lastName ?? '')).trim()" [showOnlyIfTruncated]="true">
									<span class="inline-block max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap align-bottom">
										{{ ((element.firstName ?? '') + ' ' + (element.lastName ?? '')).trim() }}
									</span>
								</ara-popover>
								@if (element.email) {
									<span class="inline-flex items-center gap-1">
										<mat-icon class="h-[14px] w-[14px] text-[14px] text-[rgba(var(--color-primary),1)]" aria-hidden="true">
											forward_to_inbox
										</mat-icon>
										<ara-popover [content]="element.email" [showOnlyIfTruncated]="true">
											<span
												class="inline-block max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap align-bottom text-xs text-gray-500">
												{{ element.email }}
											</span>
										</ara-popover>
									</span>
								}
							</div>
						</td>
					</ng-container>

					<ng-container matColumnDef="codeValidDate">
						<th *matHeaderCellDef mat-header-cell>{{ t('permissions.codeValidUntil') }}</th>
						<td [attr.data-label]="t('permissions.codeValidUntil')" *matCellDef="let element" mat-cell>
							{{ element.codeValidUntil | date: 'dd.MM.yyyy' }}
						</td>
					</ng-container>

					<ng-container matColumnDef="createDate">
						<th *matHeaderCellDef mat-header-cell>{{ t('common.created') }}</th>
						<td [attr.data-label]="t('common.created')" *matCellDef="let element" mat-cell>
							<div class="flex flex-col leading-[1.2]">
								<span>{{ element.createDate | date: 'dd.MM.yyyy' }}</span>
								<span class="text-xs text-gray-500">{{ element.createDate | date: 'HH:mm' }}</span>
							</div>
						</td>
					</ng-container>

					<ng-container matColumnDef="codeActivatedOn">
						<th *matHeaderCellDef mat-header-cell>{{ t('permissions.codeUsedOn') }}</th>
						<td [attr.data-label]="t('permissions.codeUsedOn')" *matCellDef="let element" mat-cell>
							<div class="flex flex-col leading-[1.2]">
								<span>{{ element.codeActivatedOn | date: 'dd.MM.yyyy' }}</span>
								<span class="text-xs text-gray-500">{{ element.codeActivatedOn | date: 'HH:mm' }}</span>
							</div>
						</td>
					</ng-container>

					<ng-container matColumnDef="code">
						<th *matHeaderCellDef mat-header-cell>{{ t('permissions.codes') }}</th>
						<td *matCellDef="let permission" [attr.data-label]="t('permissions.codes')" mat-cell>
							@if (!layoutService.isMobile()) {
								@if (permission.authCode && !permission.codeActivatedOn) {
									<div class="flex flex-row justify-left">
										<ara-qr-generator
											(click)="showQrCode(permission)"
											[width]="50"
											[permission]="permission"
											class="cy-show-qr-clickable clickable"></ara-qr-generator>
									</div>
									<div class="flex flex-row justify-left">
										<u class="cy-auth-code-text">{{ permission.authCode }}</u>
									</div>
								} @else if (permission.authCode && permission.codeActivatedOn) {
									<div class="flex flex-row items-center justify-left gap-1 text-gray-400">
										<u class="cy-auth-code-text">{{ permission.authCode }}</u>
										<mat-icon class="h-[18px] w-[18px] text-[18px] text-green-500" aria-label="Activated code">check_circle</mat-icon>
									</div>
								} @else {
									<span>{{ t('common.noData') }}</span>
								}
							}
							@if (layoutService.isMobile()) {
								@if (permission.authCode && !permission.codeActivatedOn) {
									<div (click)="showQrCode(permission)">
										<u>{{ permission.authCode }}</u>
									</div>
								} @else if (permission.authCode && permission.codeActivatedOn) {
									<div class="flex flex-row items-center gap-1 text-gray-400">
										<u>{{ permission.authCode }}</u>
										<mat-icon class="h-[18px] w-[18px] text-[18px] text-green-500" aria-label="Activated code">check_circle</mat-icon>
									</div>
								} @else {
									<span>{{ t('common.noData') }}</span>
								}
							}
						</td>
					</ng-container>

					<ng-container matColumnDef="comment">
						<th *matHeaderCellDef mat-header-cell>{{ t('common.comment') }}</th>
						<td
							*matCellDef="let element"
							[attr.data-label]="t('common.comment')"
							mat-cell
							class="whitespace-normal [overflow-wrap:anywhere] break-words">
							<ara-popover [content]="element.comment ?? ''" [showOnlyIfTruncated]="true">
								<span class="truncate-text-wrap">{{ element.comment }}</span>
							</ara-popover>
						</td>
					</ng-container>

					<ng-container matColumnDef="edit">
						<th *matHeaderCellDef mat-header-cell>{{ t('commonActions.edit') }}</th>
						<td *matCellDef="let element" [attr.data-label]="t('commonActions.edit')" mat-cell>
							<button
								(click)="editPermission(element)"
								aria-label="Edit"
								class="edit-button hover-button"
								color="primary"
								mat-icon-button
								title="{{ t('commonActions.edit') }}">
								<mat-icon [svgIcon]="icons.get('edit')" title="{{ t('commonActions.edit') }}"></mat-icon>
							</button>
							<button
								(click)="deletePermission(element)"
								aria-label="Delete"
								class="delete-button hover-button"
								color="primary"
								mat-icon-button
								title="{{ t('commonActions.delete') }}">
								<mat-icon [svgIcon]="icons.get('delete')" class="hover-button" title="{{ t('commonActions.delete') }}"></mat-icon>
							</button>
						</td>
					</ng-container>

					<tr *matHeaderRowDef="displayedPermissionColumns" mat-header-row></tr>
					<tr *matRowDef="let row; columns: displayedPermissionColumns" @fadeInUp mat-row></tr>
				</table>
			} @else {
				<div class="flex flex-col items-center justify-center gap-2 px-3 py-6 text-gray-500">
					<mat-icon class="text-gray-400">info</mat-icon>
					<span>{{ t('common.noData') }}</span>
				</div>
			}
		</div>
	</mat-dialog-content>
	@if (!canShowAddPermissionButton() && permissionsDisabledReason) {
		<mat-dialog-actions>
			<span class="m-auto px-3 text-sm text-gray-600">{{ permissionsDisabledReason }}</span>
		</mat-dialog-actions>
	}
	@if (canShowAddPermissionButton()) {
		<mat-dialog-actions>
			<button class="m-auto" (click)="addNewPermission()" mat-raised-button>
				<div class="ml-1">{{ t('tenantunitDetails.addNewPermission') }}</div>
				<mat-icon fontSet="material-symbols-outlined" class="qr-icon-button ml-2" aria-label="Add barcode">qr_code_2_add</mat-icon>
			</button>
		</mat-dialog-actions>
	}
</div>
